import React, { forwardRef, useImperativeHandle } from 'react';

import { Icon, Image, Text } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { View } from 'react-native';

import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { toaster } from 'components/toaster/toaster';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import {
  BackgroundAndLogoContainer,
  GiftCardContainer,
  LogoAndNameContainer,
} from 'experiments/checkout-v2/treatment/features/payment/components/add-reload-prepaid-method-modal/styled';
import { useCardNumber } from 'experiments/checkout-v2/treatment/features/payment/hooks/use-giftcard-number';
import { AddGiftCardInput } from 'experiments/checkout-v2/treatment/pages/cart/payment/add-gift-card-input';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import logger from 'utils/logger';

import { AddEditMethodProps } from './types';

const crownsImg = require('styles/images/crowns-image.png');

export type PrepaidMethodRef = {
  addNewCard: () => void;
};

export const AddPrepaidMethod = forwardRef<PrepaidMethodRef, AddEditMethodProps>(
  ({ openErrorDialog, setIsLoading, isLoading }, ref) => {
    const {
      setIsAddReloadPrepaidMethodModalVisible,
      getPrepaidPaymentMethod,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      mergePrepaidCardBalances,
    } = usePaymentContext();
    const {
      giftCardNumber,
      errors,
      handleCardNumberChange,
      isValidGiftCardNumberCheck,
      validateCardNumber,
    } = useCardNumber();

    const cardNumber = giftCardNumber?.replace(/\s+/g, '');

    useImperativeHandle(
      ref,
      () => ({
        addNewCard: async () => {
          if (getPrepaidPaymentMethod()) {
            return openErrorDialog({ message: t('giftCardExists') });
          }

          setIsLoading(true);
          handleCardNumberChange(cardNumber);
          validateCardNumber(cardNumber);

          if (!isValidGiftCardNumberCheck(cardNumber)) {
            setIsLoading(false);
            return;
          }

          try {
            if (!isLoading && !errors.giftCardNumber && cardNumber) {
              await mergePrepaidCardBalances({
                // Source pin is not required, BK crown cards don't have it
                sourceCardNumber: cardNumber,
              });

              setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
              setDefaultPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
              setIsAddReloadPrepaidMethodModalVisible(false);
              toaster.giftCardAddedToast();
            }
          } catch (error) {
            openErrorDialog({ message: t('giftCardAddingError'), error });
            logger.error({ error, message: 'Error adding gift card' });
          } finally {
            setIsLoading(false);
          }
        },
      }),
      [
        cardNumber,
        errors.giftCardNumber,
        getPrepaidPaymentMethod,
        handleCardNumberChange,
        isLoading,
        isValidGiftCardNumberCheck,
        mergePrepaidCardBalances,
        openErrorDialog,
        setCheckoutPaymentMethodId,
        setDefaultPaymentMethodId,
        setIsAddReloadPrepaidMethodModalVisible,
        setIsLoading,
        validateCardNumber,
      ]
    );

    return (
      <>
        <GiftCardContainer>
          <BackgroundAndLogoContainer>
            <LogoAndNameContainer>
              <Icon accessibilityHidden variant="giftCard" size="xl" mb="$1" />
              <Text
                accessibilityHidden
                color={primitive.$bbqBrown}
                lineHeight="24px"
                fontWeight="bold"
                fontSize="2xl"
              >
                {t('giftCard')}
              </Text>
            </LogoAndNameContainer>
            <Image accessibilityHidden source={crownsImg} width={162} height={145} marginTop="$7" />
          </BackgroundAndLogoContainer>
        </GiftCardContainer>
        <KeyboardAwareView withScroll>
          <View style={{ paddingHorizontal: spacing.$4 }}>
            <AddGiftCardInput
              onChange={handleCardNumberChange}
              errorMessage={errors?.giftCardNumber}
              label={t('cardNumber')}
              testID="giftcard-input"
              isRedesign={true}
              isLoading={isLoading}
            />
          </View>
        </KeyboardAwareView>
      </>
    );
  }
);
